import type { FC } from 'react';
import { Fragment } from 'react';
import classNames from 'classnames';

import CtSideIcon from 'app/icons/cs_side_ct.svg';
import TSideIcon from 'app/icons/cs_side_t.svg';
import DeathIcon from 'app/icons/cs_death.svg';
import { hasField } from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/utils';

import style from './style.module.scss';

// The badge glyph is the team's *side* emblem, not its logo — so it flips at
// half-time while the pill colour (board position) stays put.
const SIDE_ICONS: Record<SideCs, SVG> = {
  'counter-terrorist': CtSideIcon,
  terrorist: TSideIcon
};

// K/D/A: always three slots. A stat the trader excluded is absent from both the
// player object and available_fields, so it renders as a dash rather than
// collapsing the row's layout.
const KDA: [CsAvailableField, 'kills' | 'deaths' | 'assists'][] = [
  ['player_kills', 'kills'],
  ['player_deaths', 'deaths'],
  ['player_assists', 'assists']
];

interface Props {
  players: AggregatedPlayerCs[];
  // null when the frame carries no side — the emblem is then unknowable, but the
  // badge still distinguishes dead from not-dead.
  side: SideCs | null;
  teamIndex: 0 | 1;
  availableFields: CsAvailableField[];
}

const CsPlayerList: FC<Props> = ({ players, side, teamIndex, availableFields }) => {
  const showAlive = hasField(availableFields, 'player_alive');
  const SideIcon = side ? SIDE_ICONS[side] : null;

  // Dead beats side: a player who died still reads as dead even without a side
  // to draw. No side left just marks "alive" with a plain square.
  const renderBadgeGlyph = (dead: boolean) => {
    if (dead) return <DeathIcon className={style.badgeIcon} />;
    if (SideIcon) return <SideIcon className={style.badgeIcon} />;
    return <span className={style.badgeSquare} />;
  };

  return (
    <div className={classNames(style.list, teamIndex === 0 ? style.team1 : style.team2)}>
      {players.map((player, index) => {
        // Three states, not two: `alive: null` means the backend could not
        // name-match the player across the frame's team blocks — unknown, not
        // dead — so it gets a faded emblem rather than a skull.
        // Gated on showAlive: without player_alive in available_fields, `alive`
        // on the object can't be trusted, so no player is ever struck through.
        const dead = showAlive && player.alive === false;
        const unknown = showAlive && player.alive === null;

        return (
          <div key={`${player.name}_${index}`} className={style.row}>
            <span className={classNames(style.pill, { [style.pillDead]: dead })}>
              <span className={style.kda}>
                {KDA.map(([field, key], statIndex) => {
                  const value = hasField(availableFields, field) ? player[key] ?? null : null;
                  return (
                    <Fragment key={field}>
                      {statIndex > 0 && <span className={style.slash} />}
                      <span
                        className={classNames(style.stat, { [style.statMuted]: value === null })}
                      >
                        {value ?? '-'}
                      </span>
                    </Fragment>
                  );
                })}
              </span>
              <span className={style.name}>{player.name}</span>
            </span>
            {showAlive && (
              <span
                className={classNames(style.badge, {
                  [style.badgeDead]: dead,
                  [style.badgeUnknown]: unknown
                })}
              >
                {renderBadgeGlyph(dead)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

CsPlayerList.displayName = 'CsPlayerList';

export default CsPlayerList;
